import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import {
  allocateOldestFirst,
  DEBTOR_AGE_BANDS,
  DEBTOR_AGE_BAND_WORDS,
  formatMoney,
  moneyFromCents,
  REMITTANCE_METHOD_WORDS,
  type RemittanceMethod,
} from '@wodi/domain';
import type { AmountView, InsurerClaimListView, InsurerClaimSummary } from '@wodi/shared';
import {
  Button,
  DateField,
  EmptyState,
  LoadingBlock,
  MoneyField,
  Notice,
  PageHeader,
  Section,
  Select,
  TextField,
  useShortcut,
} from '@wodi/ui';
import { useState, type ReactNode } from 'react';

import { payersQuery } from '../api/finance.ts';
import {
  allocateRemittance,
  debtorsQuery,
  insuranceKeys,
  insurerClaimsQuery,
  invoiceQuery,
  invoicesQuery,
  issueInvoice,
  openClaimsQuery,
  preauthsQuery,
  recordRemittance,
  remittanceQuery,
  remittancesQuery,
} from '../api/insurance.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { PREAUTH_STATE_WORDS } from '../insurance/bill-insurance.tsx';
import { InvoiceSheet } from '../insurance/prints.tsx';
import styles from '../insurance/insurance.module.css';
import { screen, TableScroll } from '../layout/screen.tsx';
import { formatDateOnly, formatInstant, formatIsoDate } from '../patients/format.ts';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';

// Insurance (docs/plans/phase2-private-insurance.md §6 items 4–8): insurer claims by where
// they stand, invoices to insurers, the insurers' payments and where they went, debtors by
// age, and approval requests. Claim, invoice and payment numbers in the mono face.

const kes = (a: AmountView) => formatMoney(moneyFromCents(BigInt(a.cents)));
const kesColumn = (a: AmountView) =>
  formatMoney(moneyFromCents(BigInt(a.cents)), { showCurrency: false });

type Area = InsurerClaimListView | 'invoices' | 'payments' | 'debtors' | 'approvals';

const VIEWS: { id: InsurerClaimListView; label: string }[] = [
  { id: 'to_send', label: 'To send' },
  { id: 'with_insurer', label: 'With insurer' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'settled', label: 'Settled' },
  { id: 'all', label: 'All claims' },
];

const EMPTY: Record<InsurerClaimListView, string> = {
  to_send: 'No claims waiting to be sent. Claims are prepared from an insurer visit’s bill.',
  with_insurer: 'No claims are waiting for insurers to pay.',
  rejected: 'No rejected claims.',
  settled: 'No claims have been paid or written off yet.',
  all: 'No insurer claims yet. They are prepared from an insurer visit’s bill.',
};

function Tabs({ current }: { current: Area }) {
  const mark = (id: Area) => (current === id ? { 'aria-current': 'page' as const } : {});
  return (
    <nav aria-label="Insurance views" className={screen.tabs}>
      {VIEWS.map((v) => (
        <Link key={v.id} to="/insurance" search={{ view: v.id }} {...mark(v.id)}>
          {v.label}
        </Link>
      ))}
      <Link to="/insurance/invoices" {...mark('invoices')}>
        Invoices
      </Link>
      <Link to="/insurance/payments" {...mark('payments')}>
        Payments
      </Link>
      <Link to="/insurance/debtors" {...mark('debtors')}>
        Debtors
      </Link>
      <Link to="/insurance/approvals" {...mark('approvals')}>
        Approvals
      </Link>
    </nav>
  );
}

function Shell({
  area,
  children,
  actions,
}: {
  area: Area;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className={styles.page}>
      <PageHeader title="Insurance" {...(actions === undefined ? {} : { actions })} />
      <Tabs current={area} />
      {children}
    </div>
  );
}

function Failed({ error, retry, what }: { error: unknown; retry: () => void; what: string }) {
  return (
    <Notice
      tone="error"
      action={
        <Button dense onPress={retry}>
          Try again
        </Button>
      }
    >
      {errorText(error, `${what} couldn't be loaded. Try again.`)}
    </Notice>
  );
}

function OfflineNote({ updatedAt }: { updatedAt: number }) {
  const online = useOnline();
  if (online || updatedAt === 0) return null;
  return (
    <Notice tone="info">{`Offline, showing data loaded at ${formatInstant(new Date(updatedAt).toISOString())}.`}</Notice>
  );
}

// --- Claims --------------------------------------------------------------------------

const listRoute = getRouteApi('/_app/insurance');

function ClaimTable({ claims, label }: { claims: InsurerClaimSummary[]; label: string }) {
  return (
    <TableScroll>
      <table className={styles.table} aria-label={label}>
        <thead>
          <tr>
            <th scope="col">Claim</th>
            <th scope="col">Patient</th>
            <th scope="col">Visit</th>
            <th scope="col">Scheme</th>
            <th scope="col" className={styles.number}>
              Claimed (KES)
            </th>
            <th scope="col" className={styles.number}>
              Outstanding (KES)
            </th>
            <th scope="col">State</th>
          </tr>
        </thead>
        <tbody>
          {claims.map((c) => (
            <tr key={c.id}>
              <td>
                <Link
                  to="/insurance/claims/$claimId"
                  params={{ claimId: c.id }}
                  className={styles.id}
                >
                  {c.claimNumber}
                </Link>
              </td>
              <td>
                {c.patient.displayName} <span className={styles.id}>{c.patient.patientNumber}</span>
              </td>
              <td>{formatDateOnly(c.visit.startedAt)}</td>
              <td>{`${c.payer.name}, ${c.schemeName}`}</td>
              <td className={styles.number}>
                {c.state === 'draft' ? kesColumn(c.estimated ?? c.claimed) : kesColumn(c.claimed)}
              </td>
              <td className={styles.number}>
                {c.state === 'draft' ? '' : kesColumn(c.outstanding)}
              </td>
              <td>{c.sentence}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}

/** Draft claims of one insurer, with "Invoice ready claims" for those with nothing to fix. */
function ToSendGroup({ claims }: { claims: InsurerClaimSummary[] }) {
  const online = useOnline();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const payer = claims[0]?.payer;
  const ready = claims.filter((c) => c.problemCount === 0);
  const issue = useMutation({
    mutationFn: () =>
      issueInvoice({
        payerId: payer?.id ?? '',
        claims: ready.map((c) => ({ id: c.id, expectedVersion: c.version })),
      }),
    onSuccess: async ({ invoiceId }) => {
      toasts.show({ title: 'Invoice issued' });
      await queryClient.invalidateQueries({ queryKey: insuranceKeys.all });
      await navigate({ to: '/insurance/invoices/$invoiceId', params: { invoiceId } });
    },
  });
  if (payer === undefined) return null;
  const total = ready.reduce((s, c) => s + BigInt(c.estimated?.cents ?? '0'), 0n);
  return (
    <Section
      title={payer.name}
      {...(session !== null && can(session, 'insurance.claims.manage') && ready.length > 0
        ? {
            actions: (
              <Button
                variant="primary"
                isDisabled={!online}
                isPending={issue.isPending}
                pendingLabel="Issuing…"
                onPress={() => {
                  issue.mutate();
                }}
              >
                {`Invoice ${String(ready.length)} ready ${ready.length === 1 ? 'claim' : 'claims'}`}
              </Button>
            ),
          }
        : {})}
    >
      <p className={styles.detail}>
        {`${String(ready.length)} of ${String(claims.length)} ready to send, ${formatMoney(moneyFromCents(total))}. The others need something fixed first; open them to see what.`}
      </p>
      {issue.error === null ? null : (
        <Notice tone="error">{errorText(issue.error, "The invoice couldn't be issued.")}</Notice>
      )}
      <ClaimTable claims={claims} label={`Claims to send to ${payer.name}`} />
    </Section>
  );
}

export function InsuranceClaimsPage() {
  const { view } = listRoute.useSearch();
  const list = useQuery({ ...insurerClaimsQuery(view), refetchInterval: 60_000 });
  const groups = new Map<string, InsurerClaimSummary[]>();
  for (const c of list.data?.items ?? [])
    groups.set(c.payer.id, [...(groups.get(c.payer.id) ?? []), c]);
  return (
    <Shell area={view}>
      <OfflineNote updatedAt={list.dataUpdatedAt} />
      {list.isPending ? <LoadingBlock label="Loading claims" rows={4} /> : null}
      {list.isError ? (
        <Failed error={list.error} retry={() => void list.refetch()} what="Claims" />
      ) : null}
      {list.isSuccess && list.data.items.length === 0 ? (
        <EmptyState>{EMPTY[view]}</EmptyState>
      ) : null}
      {list.isSuccess && list.data.items.length > 0 && view === 'to_send'
        ? [...groups.values()].map((claims) => (
            <ToSendGroup key={claims[0]?.payer.id} claims={claims} />
          ))
        : null}
      {list.isSuccess && list.data.items.length > 0 && view !== 'to_send' ? (
        <Section title={VIEWS.find((v) => v.id === view)?.label ?? 'Claims'}>
          <ClaimTable claims={list.data.items} label="Insurer claims" />
        </Section>
      ) : null}
    </Shell>
  );
}

// --- Invoices ------------------------------------------------------------------------

export function InsuranceInvoicesPage() {
  const list = useQuery(invoicesQuery);
  return (
    <Shell area="invoices">
      <OfflineNote updatedAt={list.dataUpdatedAt} />
      {list.isPending ? <LoadingBlock label="Loading invoices" rows={4} /> : null}
      {list.isError ? (
        <Failed error={list.error} retry={() => void list.refetch()} what="Invoices" />
      ) : null}
      {list.isSuccess && list.data.items.length === 0 ? (
        <EmptyState>No invoices to insurers yet. They are issued from claims to send.</EmptyState>
      ) : null}
      {list.isSuccess && list.data.items.length > 0 ? (
        <Section title="Invoices to insurers">
          <TableScroll>
            <table className={styles.table} aria-label="Invoices to insurers">
              <thead>
                <tr>
                  <th scope="col">Invoice</th>
                  <th scope="col">Insurer</th>
                  <th scope="col">Issued</th>
                  <th scope="col" className={styles.number}>
                    Claims
                  </th>
                  <th scope="col" className={styles.number}>
                    Total (KES)
                  </th>
                  <th scope="col" className={styles.number}>
                    Outstanding (KES)
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((i) => (
                  <tr key={i.id}>
                    <td>
                      <Link
                        to="/insurance/invoices/$invoiceId"
                        params={{ invoiceId: i.id }}
                        className={styles.id}
                      >
                        {i.invoiceNumber}
                      </Link>
                    </td>
                    <td>{i.payer.name}</td>
                    <td>{formatDateOnly(i.issuedAt)}</td>
                    <td className={styles.number}>{i.claimCount}</td>
                    <td className={styles.number}>{kesColumn(i.total)}</td>
                    <td className={styles.number}>{kesColumn(i.outstanding)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        </Section>
      ) : null}
    </Shell>
  );
}

const invoiceRoute = getRouteApi('/_app/insurance/invoices/$invoiceId');

export function InsuranceInvoicePage() {
  const { invoiceId } = invoiceRoute.useParams();
  const invoice = useQuery(invoiceQuery(invoiceId));
  return (
    <Shell area="invoices">
      {invoice.isPending ? <LoadingBlock label="Loading the invoice" rows={4} /> : null}
      {invoice.isError ? (
        <Failed error={invoice.error} retry={() => void invoice.refetch()} what="The invoice" />
      ) : null}
      {invoice.isSuccess ? (
        <>
          <Section title="Invoice">
            <dl className={styles.facts}>
              <dt>Number</dt>
              <dd className={styles.id}>{invoice.data.invoiceNumber}</dd>
              <dt>Insurer</dt>
              <dd>{invoice.data.payer.name}</dd>
              <dt>Issued</dt>
              <dd>{`${formatInstant(invoice.data.issuedAt)} by ${invoice.data.issuedBy.displayName}`}</dd>
              <dt>Total</dt>
              <dd>{`${kes(invoice.data.total)} for ${String(invoice.data.claimCount)} ${invoice.data.claimCount === 1 ? 'claim' : 'claims'}`}</dd>
              <dt>Outstanding</dt>
              <dd>{kes(invoice.data.outstanding)}</dd>
            </dl>
          </Section>
          <InvoiceSheet invoice={invoice.data} />
        </>
      ) : null}
    </Shell>
  );
}

// --- Payments ------------------------------------------------------------------------

export function InsurancePaymentsPage() {
  const navigate = useNavigate();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const mayRecord = session !== null && can(session, 'insurance.remittance.record');
  const list = useQuery(remittancesQuery);
  useShortcut(
    'n',
    () => {
      if (mayRecord) void navigate({ to: '/insurance/payments/new' });
    },
    { description: 'Record a payment' },
  );
  return (
    <Shell
      area="payments"
      {...(mayRecord
        ? { actions: <Link to="/insurance/payments/new">Record a payment (N)</Link> }
        : {})}
    >
      <OfflineNote updatedAt={list.dataUpdatedAt} />
      {list.isPending ? <LoadingBlock label="Loading payments" rows={4} /> : null}
      {list.isError ? (
        <Failed error={list.error} retry={() => void list.refetch()} what="Payments" />
      ) : null}
      {list.isSuccess && list.data.items.length === 0 ? (
        <EmptyState>No insurer payments recorded yet. Record one when an insurer pays.</EmptyState>
      ) : null}
      {list.isSuccess && list.data.items.length > 0 ? (
        <Section title="Payments from insurers">
          <TableScroll>
            <table className={styles.table} aria-label="Payments from insurers">
              <thead>
                <tr>
                  <th scope="col">Payment</th>
                  <th scope="col">Insurer</th>
                  <th scope="col">Reference</th>
                  <th scope="col">Received</th>
                  <th scope="col" className={styles.number}>
                    Amount (KES)
                  </th>
                  <th scope="col" className={styles.number}>
                    Unapplied (KES)
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <Link
                        to="/insurance/payments/$remittanceId"
                        params={{ remittanceId: r.id }}
                        className={styles.id}
                      >
                        {r.remittanceNumber}
                      </Link>
                    </td>
                    <td>{r.payer.name}</td>
                    <td className={styles.id}>{r.reference}</td>
                    <td>{formatIsoDate(r.receivedOn)}</td>
                    <td className={styles.number}>{kesColumn(r.amount)}</td>
                    <td className={styles.number}>{kesColumn(r.unapplied)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        </Section>
      ) : null}
    </Shell>
  );
}

/** The insurer's open claims with an amount field each, and "Allocate oldest first". */
function AllocationTable({
  payerId,
  available,
  amounts,
  onChange,
}: {
  payerId: string;
  available: bigint;
  amounts: Record<string, bigint | null>;
  onChange: (next: Record<string, bigint | null>) => void;
}) {
  const open = useQuery(openClaimsQuery(payerId));
  if (open.isPending) return <LoadingBlock label="Loading open claims" rows={3} />;
  if (open.isError)
    return <Failed error={open.error} retry={() => void open.refetch()} what="Open claims" />;
  if (open.data.items.length === 0)
    return (
      <p className={styles.detail}>
        This insurer has no open claims. The payment is recorded as unapplied until claims are sent.
      </p>
    );
  const oldestFirst = () => {
    const { allocations } = allocateOldestFirst(
      moneyFromCents(available),
      open.data.items.map((c) => ({
        id: c.id,
        invoicedAt: new Date(c.invoicedAt),
        outstanding: moneyFromCents(BigInt(c.outstanding.cents)),
      })),
    );
    onChange(Object.fromEntries(allocations.map((a) => [a.claimId, a.amount.cents])));
  };
  return (
    <div className={styles.stack}>
      <div className={styles.actions}>
        <Button dense onPress={oldestFirst} isDisabled={available <= 0n}>
          Allocate oldest first
        </Button>
      </div>
      <TableScroll>
        <table className={styles.table} aria-label="Open claims">
          <thead>
            <tr>
              <th scope="col">Claim</th>
              <th scope="col">Invoice</th>
              <th scope="col">Sent</th>
              <th scope="col" className={styles.number}>
                Outstanding (KES)
              </th>
              <th scope="col">Allocate</th>
            </tr>
          </thead>
          <tbody>
            {open.data.items.map((c) => (
              <tr key={c.id}>
                <td className={styles.id}>{c.claimNumber}</td>
                <td className={styles.id}>{c.invoiceNumber ?? ''}</td>
                <td>{formatDateOnly(c.invoicedAt)}</td>
                <td className={styles.number}>{kesColumn(c.outstanding)}</td>
                <td>
                  <MoneyField
                    label={`Allocate to ${c.claimNumber}`}
                    value={amounts[c.id] ?? null}
                    onChange={(cents) => {
                      onChange({ ...amounts, [c.id]: cents });
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </div>
  );
}

function toAllocations(amounts: Record<string, bigint | null>) {
  return Object.entries(amounts)
    .filter((e): e is [string, bigint] => e[1] !== null && e[1] > 0n)
    .map(([claimId, cents]) => ({
      claimId,
      amount: { currency: 'KES' as const, cents: cents.toString() },
    }));
}

const METHODS: RemittanceMethod[] = ['eft', 'cheque', 'mpesa', 'other'];

export function RecordPaymentPage() {
  const online = useOnline();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const payers = useQuery(payersQuery);
  const insurers = (payers.data ?? []).filter((p) => p.kind === 'insurer');
  const [payerId, setPayerId] = useState<string | null>(null);
  const [reference, setReference] = useState('');
  const [method, setMethod] = useState<RemittanceMethod>('eft');
  const [receivedOn, setReceivedOn] = useState<string | null>(null);
  const [amount, setAmount] = useState<bigint | null>(null);
  const [allocations, setAllocations] = useState<Record<string, bigint | null>>({});
  const [tried, setTried] = useState(false);
  const allocated = Object.values(allocations).reduce<bigint>((s, v) => s + (v ?? 0n), 0n);
  const unapplied = (amount ?? 0n) - allocated;
  const missing =
    payerId === null ||
    reference.trim() === '' ||
    receivedOn === null ||
    amount === null ||
    amount <= 0n;
  const save = useMutation({
    mutationFn: () =>
      recordRemittance({
        payerId: payerId ?? '',
        reference: reference.trim(),
        method,
        receivedOn: receivedOn ?? '',
        amount: { currency: 'KES', cents: (amount ?? 0n).toString() },
        allocations: toAllocations(allocations),
      }),
    onSuccess: async ({ remittanceId }) => {
      toasts.show({ title: 'Payment recorded' });
      await queryClient.invalidateQueries({ queryKey: insuranceKeys.all });
      await navigate({ to: '/insurance/payments/$remittanceId', params: { remittanceId } });
    },
  });
  const submit = () => {
    setTried(true);
    if (!missing && unapplied >= 0n && online) save.mutate();
  };
  useShortcut('mod+enter', submit, { allowInFields: true, description: 'Record payment' });
  return (
    <Shell area="payments">
      <Section title="Record a payment from an insurer">
        <form
          className={styles.form}
          aria-label="Record a payment from an insurer"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          {payers.isPending ? <LoadingBlock label="Loading insurers" rows={1} /> : null}
          <div className={styles.fields}>
            <Select
              label="Insurer"
              options={insurers.map((p) => ({ id: p.id, label: p.name }))}
              value={payerId}
              onChange={(v) => {
                setPayerId(v === null ? null : String(v));
                setAllocations({});
              }}
              {...(tried && payerId === null ? { errorMessage: 'Choose the insurer.' } : {})}
            />
            <TextField
              label="Insurer’s reference"
              description="The EFT or cheque number."
              value={reference}
              onChange={setReference}
              {...(tried && reference.trim() === ''
                ? { errorMessage: 'Enter the payment reference.' }
                : {})}
            />
            <Select
              label="Paid by"
              options={METHODS.map((m) => ({ id: m, label: REMITTANCE_METHOD_WORDS[m] }))}
              value={method}
              onChange={(v) => {
                setMethod((v ?? 'eft') as RemittanceMethod);
              }}
            />
            <DateField
              label="Received on"
              value={receivedOn}
              onChange={setReceivedOn}
              {...(tried && receivedOn === null
                ? { errorMessage: 'Enter the date received.' }
                : {})}
            />
            <MoneyField
              label="Amount"
              value={amount}
              onChange={(cents) => {
                setAmount(cents);
              }}
              {...(tried && (amount === null || amount <= 0n)
                ? { errorMessage: 'Enter the amount received.' }
                : {})}
            />
          </div>
          {payerId === null ? null : (
            <AllocationTable
              payerId={payerId}
              available={amount ?? 0n}
              amounts={allocations}
              onChange={setAllocations}
            />
          )}
          <p className={styles.sentence}>
            {unapplied < 0n
              ? `The allocations are ${formatMoney(moneyFromCents(-unapplied))} more than the payment.`
              : `Allocated ${formatMoney(moneyFromCents(allocated))}. Unapplied ${formatMoney(moneyFromCents(unapplied))}.`}
          </p>
          {online ? null : (
            <Notice tone="info">
              Recording a payment needs a connection. Reconnect, then record it.
            </Notice>
          )}
          {save.error === null ? null : (
            <Notice tone="error">
              {errorText(save.error, "The payment couldn't be recorded.")}
            </Notice>
          )}
          <div className={styles.actions}>
            <Button
              type="submit"
              variant="primary"
              isDisabled={!online || unapplied < 0n}
              isPending={save.isPending}
              pendingLabel="Recording…"
            >
              Record payment
            </Button>
          </div>
        </form>
      </Section>
    </Shell>
  );
}

const paymentRoute = getRouteApi('/_app/insurance/payments/$remittanceId');

export function InsurancePaymentPage() {
  const { remittanceId } = paymentRoute.useParams();
  const online = useOnline();
  const queryClient = useQueryClient();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const payment = useQuery(remittanceQuery(remittanceId));
  const [amounts, setAmounts] = useState<Record<string, bigint | null>>({});
  const allocate = useMutation({
    mutationFn: () => allocateRemittance(remittanceId, toAllocations(amounts)),
    onSuccess: async () => {
      toasts.show({ title: 'Payment allocated' });
      setAmounts({});
      await queryClient.invalidateQueries({ queryKey: insuranceKeys.all });
    },
  });
  const unapplied = payment.data === undefined ? 0n : BigInt(payment.data.unapplied.cents);
  return (
    <Shell area="payments">
      {payment.isPending ? <LoadingBlock label="Loading the payment" rows={4} /> : null}
      {payment.isError ? (
        <Failed error={payment.error} retry={() => void payment.refetch()} what="The payment" />
      ) : null}
      {payment.isSuccess ? (
        <>
          <Section title="Payment">
            <dl className={styles.facts}>
              <dt>Number</dt>
              <dd className={styles.id}>{payment.data.remittanceNumber}</dd>
              <dt>Insurer</dt>
              <dd>{payment.data.payer.name}</dd>
              <dt>Reference</dt>
              <dd className={styles.id}>{payment.data.reference}</dd>
              <dt>Received</dt>
              <dd>{`${formatIsoDate(payment.data.receivedOn)}, ${REMITTANCE_METHOD_WORDS[payment.data.method]}`}</dd>
              <dt>Amount</dt>
              <dd>{kes(payment.data.amount)}</dd>
              <dt>Unapplied</dt>
              <dd>{kes(payment.data.unapplied)}</dd>
            </dl>
          </Section>
          <Section title="Where it went">
            {payment.data.allocations.length === 0 ? (
              <p className={styles.detail}>Not allocated to any claim yet.</p>
            ) : (
              <ul className={styles.list} aria-label="Allocations">
                {payment.data.allocations.map((a) => (
                  <li key={`${a.claimId}-${a.allocatedAt}`} className={styles.row}>
                    <Link
                      to="/insurance/claims/$claimId"
                      params={{ claimId: a.claimId }}
                      className={styles.id}
                    >
                      {a.claimNumber}
                    </Link>
                    <span>{kes(a.amount)}</span>
                    <span className={styles.detail}>{formatInstant(a.allocatedAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
          {unapplied > 0n && session !== null && can(session, 'insurance.remittance.record') ? (
            <Section title="Allocate the rest">
              <AllocationTable
                payerId={payment.data.payer.id}
                available={unapplied}
                amounts={amounts}
                onChange={setAmounts}
              />
              {allocate.error === null ? null : (
                <Notice tone="error">
                  {errorText(allocate.error, "The payment couldn't be allocated.")}
                </Notice>
              )}
              <div className={styles.actions}>
                <Button
                  variant="primary"
                  isDisabled={!online || toAllocations(amounts).length === 0}
                  isPending={allocate.isPending}
                  pendingLabel="Allocating…"
                  onPress={() => {
                    allocate.mutate();
                  }}
                >
                  Allocate
                </Button>
              </div>
            </Section>
          ) : null}
        </>
      ) : null}
    </Shell>
  );
}

// --- Debtors -------------------------------------------------------------------------

export function InsuranceDebtorsPage() {
  const debtors = useQuery(debtorsQuery);
  return (
    <Shell area="debtors">
      <OfflineNote updatedAt={debtors.dataUpdatedAt} />
      {debtors.isPending ? <LoadingBlock label="Loading debtors" rows={4} /> : null}
      {debtors.isError ? (
        <Failed error={debtors.error} retry={() => void debtors.refetch()} what="Debtors" />
      ) : null}
      {debtors.isSuccess && debtors.data.items.length === 0 ? (
        <EmptyState>
          No insurer owes anything. Sent claims waiting for payment appear here.
        </EmptyState>
      ) : null}
      {debtors.isSuccess && debtors.data.items.length > 0 ? (
        <Section title={`What insurers owe, as at ${formatIsoDate(debtors.data.asOf)}`}>
          <p className={styles.detail}>
            Age is counted from the day the claim went on an invoice to the insurer.
          </p>
          <TableScroll>
            <table className={styles.table} aria-label="Insurer debtors">
              <thead>
                <tr>
                  <th scope="col">Insurer</th>
                  {DEBTOR_AGE_BANDS.map((b) => (
                    <th key={b} scope="col" className={styles.number}>
                      {DEBTOR_AGE_BAND_WORDS[b]}
                    </th>
                  ))}
                  <th scope="col" className={styles.number}>
                    Owed (KES)
                  </th>
                  <th scope="col" className={styles.number}>
                    Unapplied (KES)
                  </th>
                  <th scope="col" className={styles.number}>
                    Not invoiced (KES)
                  </th>
                </tr>
              </thead>
              <tbody>
                {debtors.data.items.map((d) => (
                  <tr key={d.payer.id}>
                    <td>{`${d.payer.name} (${String(d.claimCount)})`}</td>
                    {DEBTOR_AGE_BANDS.map((b) => (
                      <td key={b} className={styles.number}>
                        {kesColumn(d.bands[b])}
                      </td>
                    ))}
                    <td className={styles.number}>{kesColumn(d.total)}</td>
                    <td className={styles.number}>{kesColumn(d.unapplied)}</td>
                    <td className={styles.number}>{kesColumn(d.notInvoiced)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total</td>
                  {DEBTOR_AGE_BANDS.map((b) => (
                    <td key={b} className={styles.number}>
                      {kesColumn(debtors.data.totals.bands[b])}
                    </td>
                  ))}
                  <td className={styles.number}>{kesColumn(debtors.data.totals.total)}</td>
                  <td className={styles.number}>{kesColumn(debtors.data.totals.unapplied)}</td>
                  <td className={styles.number}>{kesColumn(debtors.data.totals.notInvoiced)}</td>
                </tr>
              </tfoot>
            </table>
          </TableScroll>
        </Section>
      ) : null}
    </Shell>
  );
}

// --- Approvals -----------------------------------------------------------------------

export function InsuranceApprovalsPage() {
  const list = useQuery(preauthsQuery);
  return (
    <Shell area="approvals">
      <OfflineNote updatedAt={list.dataUpdatedAt} />
      {list.isPending ? <LoadingBlock label="Loading approval requests" rows={4} /> : null}
      {list.isError ? (
        <Failed error={list.error} retry={() => void list.refetch()} what="Approval requests" />
      ) : null}
      {list.isSuccess && list.data.items.length === 0 ? (
        <EmptyState>
          No approval requests. They are recorded from an insurer visit’s bill.
        </EmptyState>
      ) : null}
      {list.isSuccess && list.data.items.length > 0 ? (
        <Section title="Approval requests, waiting first">
          <TableScroll>
            <table className={styles.table} aria-label="Approval requests">
              <thead>
                <tr>
                  <th scope="col">Asked</th>
                  <th scope="col">Insurer</th>
                  <th scope="col">For</th>
                  <th scope="col">Reference</th>
                  <th scope="col">State</th>
                  <th scope="col">Visit</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((p) => (
                  <tr key={p.id}>
                    <td>{formatInstant(p.requestedAt)}</td>
                    <td>{p.payerName}</td>
                    <td>{p.serviceName ?? 'The whole visit'}</td>
                    <td className={styles.id}>{p.insurerReference ?? ''}</td>
                    <td>
                      {PREAUTH_STATE_WORDS[p.state]}
                      {p.decisionNote === null ? '' : `: ${p.decisionNote}`}
                    </td>
                    <td>
                      <Link to="/cashier" search={{ visitId: p.visitId }}>
                        Open the bill
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        </Section>
      ) : null}
    </Shell>
  );
}
